import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { getApiOrigin } from '../config/environment';
import CategoryIcon from '../components/CategoryIcon';
import type { Category } from '../types/category';
import type {
  HomeTickerAnnouncement,
  HomeTickerAnnouncementPayload,
  HomeTickerAnnouncementType,
} from '../types/homeTickerAnnouncement';

function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowDatetimeLocalValue(): string {
  return toDatetimeLocalValue(new Date().toISOString());
}

const emptyForm = (): HomeTickerAnnouncementPayload & { is_active: boolean; sort_order: number } => ({
  type: 'lost',
  title: '',
  category_id: null,
  location_label: '',
  display_at: nowDatetimeLocalValue(),
  first_name: '',
  is_active: true,
  sort_order: 0,
});

const HomeTickerAnnouncementsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<HomeTickerAnnouncement | null>(null);
  const [form, setForm] = React.useState(emptyForm());
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<HomeTickerAnnouncement | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['home-ticker-announcements'],
    queryFn: () => apiService.getHomeTickerAnnouncements(),
    select: (res) => res.announcements || [],
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (res) => (res.data.categories || []) as Category[],
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: HomeTickerAnnouncementPayload = {
        type: form.type,
        title: form.title.trim(),
        category_id: form.category_id ?? null,
        location_label: form.location_label.trim(),
        display_at: new Date(form.display_at).toISOString(),
        first_name: form.first_name.trim(),
        is_active: form.is_active,
        sort_order: form.sort_order,
      };

      if (editing) {
        const res = await apiService.updateHomeTickerAnnouncement(editing.id, payload);
        if (imageFile) {
          await apiService.uploadHomeTickerAnnouncementImage(editing.id, imageFile);
        }
        return res;
      }

      const res = await apiService.createHomeTickerAnnouncement(payload);
      if (imageFile && res.announcement?.id) {
        await apiService.uploadHomeTickerAnnouncementImage(res.announcement.id, imageFile);
      }
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-ticker-announcements'] });
      closeDialog();
    },
    onError: (err: any) => {
      setFormError(
        err?.response?.data?.error || err?.message || 'Erreur lors de l’enregistrement'
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteHomeTickerAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-ticker-announcements'] });
      setDeleteTarget(null);
    },
  });

  const removeImageMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteHomeTickerAnnouncementImage(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['home-ticker-announcements'] });
      if (editing && res.announcement?.id === editing.id) {
        setEditing(res.announcement);
        setImagePreview(null);
      }
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setImageFile(null);
    setImagePreview(null);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (item: HomeTickerAnnouncement) => {
    setEditing(item);
    setForm({
      type: item.type,
      title: item.title,
      category_id: item.category_id ?? null,
      location_label: item.location_label,
      display_at: toDatetimeLocalValue(item.display_at),
      first_name: item.first_name,
      is_active: item.is_active,
      sort_order: item.sort_order,
    });
    setImageFile(null);
    setImagePreview(item.image_url ? `${getApiOrigin()}${item.image_url}` : null);
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setFormError(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.title.trim() || !form.location_label.trim() || !form.first_name.trim()) {
      setFormError('Titre, lieu et prénom sont requis.');
      return;
    }
    if (!form.display_at) {
      setFormError('La date est requise.');
      return;
    }
    saveMutation.mutate();
  };

  const apiOrigin = getApiOrigin();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Annonces fictives — accueil
          </Typography>
          <Typography color="text.secondary">
            Alimentent le bandeau « En ce moment sur Sherlook ». Non rattachées à un utilisateur,
            un partenaire, et exclues du matching.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nouvelle annonce
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Impossible de charger les annonces fictives.</Alert>}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {(data || []).map((item) => {
            const thumb = item.image_url ? `${apiOrigin}${item.image_url}` : null;
            return (
              <Grid item xs={12} md={6} lg={4} key={item.id}>
                <Card variant="outlined" sx={{ opacity: item.is_active ? 1 : 0.65 }}>
                  <CardContent>
                    <Box display="flex" gap={2}>
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          overflow: 'hidden',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {thumb ? (
                          <Box
                            component="img"
                            src={thumb}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Pas d’image
                          </Typography>
                        )}
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                          <Chip
                            size="small"
                            label={item.type === 'found' ? 'Trouvé' : 'Perdu'}
                            color={item.type === 'found' ? 'secondary' : 'warning'}
                          />
                          {!item.is_active && <Chip size="small" label="Inactive" />}
                        </Box>
                        <Typography fontWeight={700} noWrap>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.first_name} · {item.location_label}
                        </Typography>
                        {item.category?.name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.category.name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(item.display_at).toLocaleString('fr-FR')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                      <IconButton size="small" onClick={() => openEdit(item)} aria-label="Modifier">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(item)}
                        aria-label="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {data && data.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">Aucune annonce fictive. Cliquez sur « Nouvelle annonce ».</Alert>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editing ? 'Modifier l’annonce fictive' : 'Nouvelle annonce fictive'}
          </DialogTitle>
          <DialogContent>
            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={form.type}
                  onChange={(_, value: HomeTickerAnnouncementType | null) => {
                    if (value) setForm((f) => ({ ...f, type: value }));
                  }}
                >
                  <ToggleButton value="lost">Perdu</ToggleButton>
                  <ToggleButton value="found">Trouvé</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Titre"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    label="Catégorie"
                    value={form.category_id ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        category_id: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Aucune</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CategoryIcon
                            icon={cat.icon}
                            customIconFilename={cat.custom_icon_filename}
                            size="small"
                          />
                          {cat.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Lieu"
                  value={form.location_label}
                  onChange={(e) => setForm((f) => ({ ...f, location_label: e.target.value }))}
                  placeholder="Ex. Paris 11e"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Prénom"
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="datetime-local"
                  label="Date affichée"
                  value={form.display_at}
                  onChange={(e) => setForm((f) => ({ ...f, display_at: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ordre d’affichage"
                  value={form.sort_order}
                  onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
                  helperText="Plus élevé = prioritaire dans le bandeau"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                  }
                  label="Active (visible sur l’accueil)"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Image
                </Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  {imagePreview && (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt=""
                      sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1 }}
                    />
                  )}
                  <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />}>
                    {imagePreview ? 'Changer l’image' : 'Ajouter une image'}
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} />
                  </Button>
                  {editing?.image_url && !imageFile && (
                    <Button
                      type="button"
                      color="error"
                      variant="text"
                      onClick={() => removeImageMutation.mutate(editing.id)}
                      disabled={removeImageMutation.isPending}
                    >
                      Supprimer l’image
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closeDialog}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <CircularProgress size={20} /> : editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Supprimer cette annonce fictive ?</DialogTitle>
        <DialogContent>
          <Typography>
            « {deleteTarget?.title} » sera définitivement supprimée du bandeau d’accueil.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeTickerAnnouncementsPage;
